import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    email: searchParams.get('email') || '',
    code: '',
    new_password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, form);
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="pt-32 pb-24 min-h-screen flex items-center" data-testid="reset-success">
        <div className="chaleur-container">
          <div className="max-w-md mx-auto text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-50 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-light mb-4">Password Reset</h1>
            <p className="text-muted-foreground mb-8">
              Your password has been updated. You can now sign in with your new password.
            </p>
            <Button
              className="btn-chaleur-primary w-full"
              onClick={() => navigate('/login')}
              data-testid="go-to-login-btn"
            >
              Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 min-h-screen flex items-center" data-testid="reset-password-page">
      <div className="chaleur-container">
        <div className="max-w-md mx-auto">
          <Link to="/forgot-password" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Link>

          <div className="text-center mb-12">
            <h1 className="text-3xl font-light mb-4">Enter Reset Code</h1>
            <p className="text-muted-foreground">Enter the 6-digit code and your new password</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-sm mb-2 block">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input-chaleur"
                required
                placeholder="your@email.com"
                data-testid="reset-email"
              />
            </div>

            <div>
              <Label htmlFor="code" className="text-sm mb-2 block">Reset Code</Label>
              <Input
                id="code"
                type="text"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                className="input-chaleur text-center text-2xl tracking-[0.5em] font-mono"
                required
                maxLength={6}
                placeholder="------"
                data-testid="reset-code"
              />
            </div>

            <div>
              <Label htmlFor="new_password" className="text-sm mb-2 block">New Password</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.new_password}
                  onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                  className="input-chaleur pr-10"
                  required
                  minLength={8}
                  placeholder="Minimum 8 characters"
                  data-testid="reset-new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {form.new_password.length > 0 && form.new_password.length < 8 && (
                <p className="text-xs text-red-500 mt-1">Password must be at least 8 characters</p>
              )}
            </div>

            <Button type="submit" className="btn-chaleur-primary w-full" disabled={loading || form.code.length !== 6} data-testid="reset-submit-btn">
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              Remember your password?{' '}
              <Link to="/login" className="text-primary hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
